import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Button, Table, Tag, Select, DatePicker, Space, Input, Checkbox, Tooltip, Segmented, message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import ErrorCreateModal from '../components/ErrorCreateModal';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'Draft',        value: 'assigned'          },
  { label: 'Completed',    value: 'completed'         },
  { label: 'Disputed',     value: 'rescore_requested' },
  { label: 'Canceled',     value: 'canceled'          },
];

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};

const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Disputed',
  canceled:          'Canceled',
};

// Survives React Router navigation; cleared on full page refresh.
let _cache = null;

// Quick toggles above the filters (as on Help Desk), each searching at once.
// Status: the toggle (Open by default = Draft and Disputed, the old default)
// unless statuses are picked in the dropdown, which then win; a toggle click
// clears the dropdown. Assigned: no toggle lit when the dropdown holds a
// person.
const STATUS_PRESETS = { open: ['assigned', 'rescore_requested'], completed: ['completed'], all: [] };
const ASSIGNED_PRESETS = ['any', 'me', 'none', 'me_or_none'];

export default function ErrorsPage() {
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);
  const canEdit     = (currentUser?.CanEditErrors ?? 0) >= 2;

  const [rows,          setRows]          = useState(() => _cache?.rows     ?? []);
  const [loading,       setLoading]       = useState(false);
  const [filterOpts,    setFilterOpts]    = useState({ campaigns: [], forms: [], assigned_tos: [] });
  const [statusMode,    setStatusMode]    = useState(() => _cache?.statusMode   ?? 'open');
  const [status,        setStatus]        = useState(() => _cache?.status       ?? []);
  const [campaign,      setCampaign]      = useState(() => _cache?.campaign     ?? []);
  const [agentName,     setAgentName]     = useState(() => _cache?.agentName    ?? '');
  const [externalRef,   setExternalRef]   = useState(() => _cache?.externalRef  ?? '');
  const [errorId,       setErrorId]       = useState(() => _cache?.errorId      ?? '');
  const [formName,      setFormName]      = useState(() => _cache?.formName     ?? null);
  const [assignedToId,  setAssignedToId]  = useState(() => _cache?.assignedToId ?? null);
  const [dateRange,     setDateRange]     = useState(() => _cache?.dateRange         ?? null);
  const [notAcknowledged, setNotAcknowledged] = useState(() => _cache?.notAcknowledged ?? false);

  const [createOpen,    setCreateOpen]    = useState(false);

  useEffect(() => {
    api.get('/api/errors/filter-options')
      .then((r) => setFilterOpts(r.data))
      .catch(() => {});
    if (_cache) load(); // eslint-disable-line
  }, []); // eslint-disable-line

  // over: filter values to search with now (a quick toggle) before the state
  // holding them has updated.
  const load = useCallback(async (over = {}) => {
    const mode = over.statusMode ?? statusMode;
    const picked = over.status ?? status;
    const st = picked.length ? picked : STATUS_PRESETS[mode];
    const at = 'assignedToId' in over ? over.assignedToId : assignedToId;
    setLoading(true);
    try {
      const params = {};
      // Error ID: find that submission whatever the other filters say.
      const idOnly = errorId.trim().replace(/^#/, '');
      if (idOnly) params.submission_id = idOnly;
      else {
      if (st.length)           params.status       = st.join(',');
      if (campaign.length)     params.campaign     = campaign.join(',');
      if (agentName.trim())    params.agent_name          = agentName.trim();
      if (externalRef.trim())  params.external_ref        = externalRef.trim();
      if (formName)            params.form_name           = formName;
      if (at)                  params.assigned_to_user_id = at;
      if (dateRange?.[0])   params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1])   params.end_date   = dateRange[1].format('YYYY-MM-DD');
      if (notAcknowledged)  params.not_acknowledged = '1';
      }
      const res = await api.get('/api/errors', { params });
      setRows(res.data);
      _cache = { rows: res.data, statusMode: mode, status: picked, campaign, agentName, externalRef, errorId, formName,
        assignedToId: at, dateRange, notAcknowledged };
    } catch {
      message.error('Failed to load error submissions.');
    } finally {
      setLoading(false);
    }
  }, [statusMode, status, campaign, agentName, externalRef, errorId, formName, assignedToId, dateRange, notAcknowledged]);

  const pickStatus = (k) => { setStatusMode(k); setStatus([]); load({ statusMode: k, status: [] }); };
  const pickAssigned = (k) => { const at = k === 'any' ? null : k; setAssignedToId(at); load({ assignedToId: at }); };



  const strSorter  = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
  const numSorter  = (key) => (a, b) => (a[key] ?? 0) - (b[key] ?? 0);

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 70, sorter: numSorter('ID') },
    { title: 'Created Date',  dataIndex: 'CreateDate',  key: 'CreateDate',  width: 115, sorter: strSorter('CreateDate') },
    { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 115, sorter: strSorter('DateOfError') },
    { title: 'External Ref', dataIndex: 'ExternalRef', key: 'ExternalRef', width: 160,
      sorter: strSorter('ExternalRef'),
      render: (v) => {
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        if (v.length <= 20) return v;
        return <Tooltip title={v}><span style={{ cursor: 'default' }}>{v.slice(0, 20)}…</span></Tooltip>;
      } },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 120,
      sorter: strSorter('Status'),
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Tag>,
    },
    { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 140, sorter: strSorter('AssignedToName') },
    { title: 'Agent',    dataIndex: 'AgentName',     key: 'AgentName',     ellipsis: true, sorter: strSorter('AgentName') },
    { title: 'Campaign', dataIndex: 'Campaign_Name', key: 'Campaign_Name', ellipsis: true, sorter: strSorter('Campaign_Name') },
    { title: 'Form',     dataIndex: 'FormName',      key: 'FormName',      ellipsis: true, sorter: strSorter('FormName') },
    {
      title: 'Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 80, align: 'right',
      sorter: numSorter('TotalErrors'),
      render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <Tag>{v}</Tag>,
    },
    {
      title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 95, align: 'right',
      sorter: numSorter('TotalFeedbacks'),
      render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>{v}</Tag>,
    },
    { title: 'Source', dataIndex: 'Source', key: 'Source', width: 130, ellipsis: true, sorter: strSorter('Source'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    {
      title: 'Coaching', key: 'Coaching', width: 160,
      sorter: (a, b) => (a.CoachingStatus ?? '').localeCompare(b.CoachingStatus ?? ''),
      render: (_, r) => {
        if (!r.CoachingStatus) return null;
        const COLOR = { draft: 'default', active: 'blue', completed: 'green' };
        const LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed' };
        return (
          <Space size={4} direction="vertical" style={{ gap: 2 }}>
            {r.CoachingRequestType && (
              <Typography.Text style={{ fontSize: 12 }}>{r.CoachingRequestType}</Typography.Text>
            )}
            <Tag color={COLOR[r.CoachingStatus] ?? 'default'} style={{ margin: 0 }}>
              {LABEL[r.CoachingStatus] ?? r.CoachingStatus}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: 'Acknowledged Date', dataIndex: 'AcknowledgedAt', key: 'AcknowledgedAt', width: 140,
      sorter: (a, b) => (a.AcknowledgedAt || '').localeCompare(b.AcknowledgedAt || ''),
      render: (v) => v
        ? <Typography.Text type="secondary">{v}</Typography.Text>
        : null,
    },
    {
      title: 'Acknowledge Deadline', key: 'AcknowledgeDeadline', width: 140,
      sorter: (a, b) => {
        const dA = a.Status === 'completed' && a.CompletedAt ? dayjs(a.CompletedAt).add(a.DisputeWindowDays ?? 4, 'day').valueOf() : 0;
        const dB = b.Status === 'completed' && b.CompletedAt ? dayjs(b.CompletedAt).add(b.DisputeWindowDays ?? 4, 'day').valueOf() : 0;
        return dA - dB;
      },
      render: (_, r) => {
        if (r.Status !== 'completed' || !r.CompletedAt) return null;
        const deadline = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
        const passed   = dayjs().isAfter(deadline);
        return (
          <Typography.Text type={passed ? 'secondary' : undefined} style={passed ? undefined : { color: '#fa8c16' }}>
            {deadline.format('YYYY-MM-DD')}
          </Typography.Text>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Error Tracking</Typography.Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create Error
          </Button>
        )}
      </div>

      <Space wrap size={16} style={{ marginBottom: 12 }}>
        <Segmented
          value={status.length ? null : statusMode}
          onChange={pickStatus}
          options={[{ value: 'open', label: 'Open' }, { value: 'completed', label: 'Completed' }, { value: 'all', label: 'All' }]}
        />
        <Segmented
          value={ASSIGNED_PRESETS.includes(assignedToId ?? 'any') ? (assignedToId ?? 'any') : null}
          onChange={pickAssigned}
          options={[{ value: 'any', label: 'Anyone' }, { value: 'me', label: 'Assigned to Me' },
                    { value: 'none', label: 'Unassigned' }, { value: 'me_or_none', label: 'Me + Unassigned' }]}
        />
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Space.Compact>
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '0 11px', border: '1px solid #d9d9d9',
            borderRight: 'none', borderRadius: '6px 0 0 6px',
            background: '#fafafa', fontSize: 14, whiteSpace: 'nowrap',
            lineHeight: '30px', height: 32,
          }}>Status</span>
          <Select
            mode="multiple"
            allowClear
            placeholder="Specific statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            maxTagCount="responsive"
            style={{ minWidth: 240 }}
            styles={{ popup: { root: { minWidth: 180 } } }}
          />
        </Space.Compact>
        <Checkbox
          checked={notAcknowledged}
          onChange={(e) => setNotAcknowledged(e.target.checked)}
        >
          Not Acknowledged
        </Checkbox>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="All campaigns"
          options={filterOpts.campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
          value={campaign}
          onChange={setCampaign}
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
        />
        <Select
          allowClear
          placeholder="All forms"
          options={filterOpts.forms.map((f) => ({ label: f.name, value: f.name }))}
          value={formName}
          onChange={setFormName}
          style={{ minWidth: 200 }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Assigned To"
          options={[
            { label: 'Me', value: 'me' },
            { label: 'Unassigned', value: 'none' },
            { label: 'Me + Unassigned', value: 'me_or_none' },
            ...(filterOpts.assigned_tos ?? []).map((u) => ({ label: u.name, value: u.id })),
          ]}
          value={assignedToId}
          onChange={setAssignedToId}
          style={{ minWidth: 180 }}
        />
        <Input.Search
          placeholder="Agent name"
          allowClear
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          onSearch={() => load()}
          style={{ width: 180 }}
        />
        <Input
          placeholder="Error ID"
          allowClear
          value={errorId}
          onChange={(e) => setErrorId(e.target.value)}
          onPressEnter={() => load()}
          style={{ width: 110 }}
        />
        <Input.Search
          placeholder="External Ref"
          allowClear
          value={externalRef}
          onChange={(e) => setExternalRef(e.target.value)}
          onSearch={() => load()}
          style={{ width: 160 }}
        />
        <RangePicker
          placeholder={['Date From', 'Date To']}
          value={dateRange}
          onChange={setDateRange}
          format="YYYY-MM-DD"
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={() => load()} loading={loading}>Search</Button>
      </Space>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        sticky
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} submission${t === 1 ? '' : 's'}` }}
        onRow={(r) => ({ onClick: () => navigate(`/errors/${r.ID}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'No error submissions found.' }}
      />

      <ErrorCreateModal
        open={createOpen}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
}
